import React from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  Slider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  CloudDownload as CloudDownloadIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import SettingsRowShell from './SettingsRowShell';

const THRESHOLD_MARKS = [
  { value: 0, label: '0%' },
  { value: 25, label: '25%' },
  { value: 50, label: '50%' },
  { value: 75, label: '75%' },
  { value: 100, label: '100%' },
];

const getThresholdColor = (val) => {
  if (val < 50) return 'success';
  if (val < 75) return 'warning';
  return 'error';
};

const SimilarityRow = ({
  threshold,
  setThreshold,
  selectedModel,
  setSelectedModel,
  supportedModels,
  modelsStatus,
  canEdit,
  saving,
  onSave,
}) => {
  const getModelStatusChip = (modelName) => {
    const s = modelsStatus[modelName];
    if (!s) return null;
    const cfg = {
      loaded:         { color: 'success', label: 'Active',         icon: <CheckCircleIcon fontSize="small" /> },
      downloaded:     { color: 'info',    label: 'Ready',          icon: <CloudDownloadIcon fontSize="small" /> },
      loading:        { color: 'warning', label: 'Loading…',       icon: <CircularProgress size={14} /> },
      not_downloaded: { color: 'default', label: 'Not Downloaded', icon: null },
      error:          { color: 'error',   label: 'Error',          icon: <WarningIcon fontSize="small" /> },
    };
    const c = cfg[s.status] || cfg.not_downloaded;
    return (
      <Chip
        icon={c.icon}
        label={c.label}
        color={c.color}
        size="small"
        variant={s.status === 'loaded' ? 'filled' : 'outlined'}
      />
    );
  };

  return (
    <SettingsRowShell
      icon={<SpeedIcon />}
      title="Similarity Settings"
      description="Threshold and embedding model for duplicate detection"
      status={{ label: `${threshold}%`, color: getThresholdColor(threshold) }}
    >
      {/* Threshold Slider */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="medium">
            Similarity Threshold
          </Typography>
          <Chip
            label={`${threshold}%`}
            color={getThresholdColor(threshold)}
            size="small"
            sx={{ fontWeight: 'bold', minWidth: 52 }}
          />
        </Box>
        <Slider
          value={threshold}
          onChange={(_, val) => setThreshold(val)}
          min={0}
          max={100}
          marks={THRESHOLD_MARKS}
          valueLabelDisplay="auto"
          disabled={!canEdit}
          sx={{
            '& .MuiSlider-thumb': { width: 20, height: 20 },
            '& .MuiSlider-track': { height: 6 },
            '& .MuiSlider-rail': { height: 6 },
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          Test cases with similarity above this threshold will be flagged as potential duplicates during publish preview.
        </Typography>
      </Box>

      {/* Model Selection */}
      <Box>
        <Typography variant="subtitle2" fontWeight="medium" sx={{ mb: 1.5 }}>
          Embedding Model
        </Typography>
        <FormControl component="fieldset" fullWidth disabled={!canEdit}>
          <RadioGroup
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
          >
            {Object.entries(supportedModels).map(([modelName, config]) => (
              <Paper
                key={modelName}
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 1,
                  cursor: canEdit ? 'pointer' : 'default',
                  borderWidth: selectedModel === modelName ? 2 : 1,
                  borderColor: selectedModel === modelName ? 'primary.main' : 'divider',
                  borderRadius: 1.5,
                  transition: 'all 0.2s',
                  opacity: canEdit ? 1 : 0.7,
                  '&:hover': canEdit ? { borderColor: 'primary.light' } : {},
                }}
                onClick={() => canEdit && setSelectedModel(modelName)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <FormControlLabel
                    value={modelName}
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="bold">{config.display_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{config.description}</Typography>
                      </Box>
                    }
                    sx={{ m: 0, flexGrow: 1 }}
                  />
                  {getModelStatusChip(modelName)}
                </Box>
              </Paper>
            ))}
          </RadioGroup>
        </FormControl>
      </Box>

      {/* Save Button */}
      {canEdit && (
        <Button
          variant="contained"
          fullWidth
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={onSave}
          disabled={saving}
          sx={{ mt: 2 }}
        >
          {saving ? 'Saving…' : 'Save Settings'}
        </Button>
      )}
    </SettingsRowShell>
  );
};

export default SimilarityRow;
